import React from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";          // 🆕 i18n import
import { useAuth } from "../../contexts/AuthProvider";

const SuppliersList = ({ suppliers, del }) => {
  const { t, i18n } = useTranslation("supplierlist");        // 🆕 namespace
  const { userRole } = useAuth();
  const lang = i18n.language;

  /* -------- table headers -------- */
  const header = [
    t("headers.entity"),                          
    t("headers.type"),                                 
    t("headers.city")                                 
  ];

  const pClass =
    "max-w-[250px] w-[250px] h-[19px] font-inter font-semibold text-base leading-[19px] text-[#464646]";
  const [drop, setDrop] = React.useState(false);
  const navigate = useNavigate();

  const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return value ? [String(value)] : [];
  };

  const renderOverflowValue = (value, maxVisibleItems = 2) => {
    const allItems = normalizeMultiValue(value);
    if (allItems.length === 0) return "-";
    if (allItems.length <= maxVisibleItems) return allItems.join(", ");

    const visibleItems = allItems.slice(0, maxVisibleItems);
    const hiddenItems = allItems.slice(maxVisibleItems);
    const hiddenTooltipText = hiddenItems.join(", ");

    return (
      <span className="inline-flex items-center gap-1 justify-center relative group">
        <span>{visibleItems.join(", ")}</span>
        <span className="text-[#5507CF] font-bold">+{hiddenItems.length}</span>
        <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap rounded bg-[#1f1f1f] px-2 py-1 text-[12px] leading-4 text-white z-20">
          {hiddenTooltipText}
        </span>
      </span>
    );
  };

  return (
    <div className={"first flex-col w-full"}>
      {/* ------------ header row ------------ */}
      <div className="box-border flex flex-row justify-between items-center p-1 px-6 gap-4 w-full h-[41px] bg-[#EBE5FF] border-b border-[#5507CF]">
        {header.map((item, index) => (
          <div
            key={index}
            className={`flex flex-col justify-center items-center p-2 gap-2 h-[33px] ${
              index === 0 ? "w-[120px] ml-[-8px]" : "w-[200px] ml-[4px]"
            }`}
          >
            <p className="max-w-[250px] h-[17px] font-inter font-normal text-sm leading-[17px] text-[#5507CF]">
              {item}
            </p>
          </div>
        ))}

        {/* Action header - only show if user is not viewer */}
        {userRole !== 'viewer' && (
          <div className="flex flex-col justify-center items-center p-2 gap-2 h-[33px] w-[120px]">
            <p className="max-w-[250px] h-[17px] font-inter font-normal text-sm leading-[17px] text-[#5507CF]">
              {t("headers.action")}                     
            </p>
          </div>
        )}
      </div>

      {/* ------------ data rows ------------ */}
      <div className="flex flex-col gap-0 w-full">
        {suppliers.map((supplier, index) => (
          <div
            key={index}
            className="box-border cursor-pointer flex flex-row justify-between items-center p-2 px-6 gap-4 w-full h-[56px] bg-white border-b-2 border-b-[#EBE5FF]"
            onClick={() => navigate(`/suppliers/${supplier.id}`)}
          >
            {/* Entity name */}
            <div className="flex flex-col justify-center items-start p-2 gap-2 h-[33px] w-[120px]">
              <p className={pClass}>{supplier.name}</p>
            </div>

            {/* Type */}
            <div className="flex flex-col justify-center items-center p-2 gap-2 h-[33px] w-[200px]">
              <p className={`${pClass} text-center`}>
                {renderOverflowValue(
                  supplier.service_type_names && supplier.service_type_names.length
                    ? supplier.service_type_names
                    : supplier.service_types && supplier.service_types.length
                    ? supplier.service_types
                    : supplier.service_type
                )}
              </p>
            </div>

            {/* City */}
            <div className="flex flex-col justify-center items-center p-2 gap-2 h-[33px] w-[200px]">
              <p className={`${pClass} text-center`}>
                {renderOverflowValue(
                  supplier.city_names && supplier.city_names.length
                    ? supplier.city_names
                    : supplier.cities && supplier.cities.length
                    ? supplier.cities
                    : supplier.city
                )}
              </p>
            </div>

            {/* Action dropdown - only show if user is not viewer */}
            {userRole !== 'viewer' && (
              <div className="relative flex flex-col justify-center items-center p-2 gap-2 h-[33px] w-[120px]">
                <img
                  src="/more-circle.svg"
                  alt={t("alt.more")}                       
                  onClick={(e) => {
                    e.stopPropagation();
                    setDrop(supplier.id);
                  }}
                  className="cursor-pointer"
                />
                {drop === supplier.id && (
                  <div className={`absolute ${lang === "ar" ? "left-[70px]" : "right-[70px]"} top-[14px] bg-white border border-[#ddd] rounded-lg shadow-md z-10 min-w-[120px]`}>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/suppliers/edite-supplier/${supplier.id}`);
                      }}
                      className="px-3 py-2 cursor-pointer text-sm hover:bg-[#f1f1f1]"
                    >
                      {t("dropdown.edit")}                  
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        del(drop);
                        setDrop(false);
                      }}
                      className="px-3 py-2 text-red cursor-pointer text-sm hover:bg-[#f1f1f1]"
                    >
                      {t("dropdown.delete")}               
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuppliersList;
